/**
 * Who is signed in.
 *
 * One store for the whole app: the router guard reads it, the cabinet reads it,
 * and every 401 from anywhere drops it back to `anonymous` so an expired cookie
 * cannot leave the UI pretending to be signed in.
 */
import { useEffect } from 'react'
import { create } from 'zustand'
import { onUnauthorized, request } from '@/shared/api/client'
import type { Lang } from '@/i18n/translations'
import type { HandleCheck } from './handle'

export interface Account {
  id: number
  email: string
  name: string
  picture: string | null
  emailVerified: boolean
  hasPassword: boolean
  /**
   * The professional half, or null for an account that is only a client.
   * There is no role column on the server either — «is a psychologist» IS the
   * existence of a catalog profile, and this is how that fact reaches the UI.
   */
  pro: ProAccount | null
  /**
   * True for an address in the server's moderator list. It only decides whether
   * the moderation screen renders — every /api/admin path checks the same list
   * for itself, so flipping this in a browser buys nothing.
   */
  moderator: boolean
  /**
   * The language the server writes this person's letters in, null until it
   * knows. Optional: an older API does not send it.
   */
  lang?: Lang | null
  /** The IANA zone the times in those letters are given in; null until known. */
  timeZone?: string | null
  /**
   * The account's public address — `hub4psy.com/{username}` — or null while
   * none is chosen (29.09.2026: EVERY account may have one, not only a
   * psychologist; for a psychologist it IS the profile's handle). Optional:
   * an older API does not send it.
   */
  username?: string | null
  /** Whether the person's own page is shown at that address. Off by default. */
  pageEnabled?: boolean
  /**
   * Whether the page shows the number of sessions with a live psychologist —
   * health data (the fact of therapy), so it is a separate switch, off by
   * default.
   */
  pageShowTherapy?: boolean
  /**
   * What the address opens: the psychologist's page (the profile is listed),
   * the person's own page (enabled, with a username) or nothing.
   */
  pageKind?: 'psychologist' | 'user' | 'none'
}

/** What `updatePage` tells the server — only the switches that moved. */
export interface PagePatch {
  pageEnabled?: boolean
  pageShowTherapy?: boolean
}

/** What `syncLocale` tells the server — only the fields that differ. */
export interface LocalePatch {
  lang?: Lang
  timeZone?: string
}

/**
 * The device's IANA zone, or undefined when the engine does not name one —
 * some answer `Etc/Unknown` for an unset system zone, and the server would
 * refuse that (and the language riding in the same request with it).
 */
export function browserTimeZone(): string | undefined {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone
    return zone && zone !== 'Etc/Unknown' ? zone : undefined
  } catch {
    return undefined
  }
}

/**
 * The engine's own spelling of a zone. The server stores the canonical IANA
 * name (`Asia/Kolkata`, `Europe/Kyiv`) while Chrome reports the CLDR one
 * (`Asia/Calcutta`, `Europe/Kiev`); compared as strings, the two would send
 * the same zone again on every load.
 */
function zoneKey(zone: string): string {
  try {
    return new Intl.DateTimeFormat('en', { timeZone: zone }).resolvedOptions().timeZone
  } catch {
    return zone
  }
}

function sameZone(known: string | null | undefined, device: string): boolean {
  return Boolean(known) && (known === device || zoneKey(known as string) === zoneKey(device))
}

/**
 * What the server should learn about this person's language and zone — or
 * null when it already knows.
 *
 * `chosen` says whether the language was picked with the switch. A choice is
 * news and replaces what the server holds; a language the screen merely fell
 * back to (the storefront's, or the English default of a cabinet opened in a
 * fresh tab) only FILLS a blank. Otherwise a confirmation link opened in a new
 * tab — English there, since the link carries no language — would overwrite
 * the Russian the person registered in, and their letters would switch
 * language behind their back. The zone is always the device's own: it is
 * where the person reads the time.
 */
export function localePatch(
  user: Pick<Account, 'lang' | 'timeZone'>,
  lang: Lang,
  chosen: boolean,
  timeZone: string | undefined,
): LocalePatch | null {
  const patch: LocalePatch = {}
  if (user.lang !== lang && (chosen || !user.lang)) patch.lang = lang
  if (timeZone && !sameZone(user.timeZone, timeZone)) patch.timeZone = timeZone
  return patch.lang === undefined && patch.timeZone === undefined ? null : patch
}

/** The account a write answered with, when it answered with one (it may be empty). */
function asAccount(answer: unknown): Account | null {
  return answer && typeof answer === 'object' && 'id' in answer ? (answer as Account) : null
}

export interface ProAccount {
  username: string
  verification: string
  listed: boolean
}

export type SessionStatus = 'loading' | 'authenticated' | 'anonymous'

interface SessionState {
  status: SessionStatus
  user: Account | null
  /** Boot probe: asks the server who we are, once. */
  load(): Promise<void>
  signIn(email: string, password: string): Promise<void>
  register(
    email: string,
    name: string,
    password: string,
    lang: string,
    /** Анти-бот экран: ханипот и возраст формы — см. MailGuard на сервере. */
    screen?: { website?: string; formMs?: number },
  ): Promise<void>
  resendVerification(email: string, lang: string): Promise<void>
  verifyEmail(token: string): Promise<void>
  forgotPassword(email: string, lang: string): Promise<void>
  resetPassword(token: string, password: string): Promise<void>
  /**
   * Also SETS the first password of an account that has none. A wrong current
   * password answers 401, which is kept quiet here: broadcast, it flipped the
   * whole app to «signed out» over a typo while the cookie was perfectly alive.
   */
  changePassword(currentPassword: string, newPassword: string): Promise<void>
  /** The name psychologists and clients see. `PATCH /api/me {name}`. */
  updateName(name: string): Promise<void>
  /**
   * Tells the server the language and zone this person reads in —
   * `PATCH /api/me {lang?, timeZone?}` — when they differ from what `/me` said
   * (rules in `localePatch`). The letters are written from these two, and the
   * browser is the only one who knows them. Called by the language switch
   * (`LanguageProvider`) on sign-in and on every RU·EN press, never by a screen.
   *
   * Quiet by design: one attempt, never throws, never shows anything. An API
   * that predates the fields answers 400 (or 405) and the app goes on exactly
   * as before; a 401 here is not broadcast — a background nicety must not be
   * what signs somebody out.
   */
  syncLocale(lang: Lang, chosen: boolean): Promise<void>
  /**
   * «Is this name free?» — `GET /api/me/username/available`. Advice, and the
   * field that shows it says so: the name is taken at the moment of the
   * claim, by a unique index, the only thing that wins the race between two
   * people typing the same word.
   */
  checkUsername(candidate: string, signal?: AbortSignal): Promise<HandleCheck>
  /**
   * Takes the address — `POST /api/me/username`. Its own call rather than a
   * field of the account patch, on purpose: a public address must never move
   * as a side effect of saving something else. For a psychologist this is the
   * same handle the profile shows, with the same rename budget.
   */
  updateUsername(username: string): Promise<void>
  /** The page switches — `PATCH /api/me {pageEnabled?, pageShowTherapy?}`. */
  updatePage(patch: PagePatch): Promise<void>
  // Signing out and deleting the account are not here: both LEAVE the page
  // (`sign-out.ts`), and flipping this store first raced the gate's redirect.
}

/**
 * The boot probe, shared. Two callers now ask for it — the auth gate of a
 * protected zone and the marketing navbar, which needs to know whether its logo
 * leads home or into the cabinet — and neither should cost a second /me.
 */
let probe: Promise<void> | null = null

/**
 * Locale syncs run one after another: a sign-in and a quick press of the
 * switch must not race, or the slower answer would leave the account showing
 * the language the person just switched away from. Each one compares against
 * the account as the previous one left it, so a repeat sends nothing.
 */
let localeQueue: Promise<void> = Promise.resolve()

/**
 * "Is anyone signed in?", for a screen that is not behind AuthGate.
 *
 * The marketing side needs the answer for two decisions — where its logo goes
 * and whether the homepage belongs to this visitor at all — and neither may
 * depend on some OTHER component having mounted first. Safe to call from as
 * many places as need it: the probe underneath is shared.
 */
export function useSessionProbe(): SessionStatus {
  const status = useSession((session) => session.status)
  const load = useSession((session) => session.load)
  useEffect(() => {
    if (status === 'loading') void load()
  }, [status, load])
  return status
}

export const useSession = create<SessionState>((set, get) => ({
  status: 'loading',
  user: null,

  async load() {
    probe ??= (async () => {
      try {
        // Anonymous is the expected answer here, so this one 401 stays quiet.
        const user = await request<Account>('/me', { quiet401: true })
        set({ status: 'authenticated', user })
      } catch {
        set({ status: 'anonymous', user: null })
      }
    })()
    return probe
  },

  async signIn(email, password) {
    const user = await request<Account>('/auth/login', {
      method: 'POST',
      body: { email, password },
    })
    set({ status: 'authenticated', user })
  },

  async register(email, name, password, lang, screen) {
    // The zone rides along with the language: the very first letters (and
    // every session letter after them) name times in it. An API that does not
    // know the field ignores it.
    await request<void>('/auth/register', {
      method: 'POST',
      body: { email, name, password, lang, timeZone: browserTimeZone(), ...screen },
    })
  },

  async resendVerification(email, lang) {
    await request<void>('/auth/resend-verification', { method: 'POST', body: { email, lang } })
  },

  async verifyEmail(token) {
    const user = await request<Account>('/auth/verify-email', { method: 'POST', body: { token } })
    set({ status: 'authenticated', user })
  },

  async forgotPassword(email, lang) {
    await request<void>('/auth/forgot-password', { method: 'POST', body: { email, lang } })
  },

  async resetPassword(token, password) {
    const user = await request<Account>('/auth/reset-password', {
      method: 'POST',
      body: { token, password },
    })
    set({ status: 'authenticated', user })
  },

  async changePassword(currentPassword, newPassword) {
    // The server ends every session and hands back a fresh cookie for this
    // device, so the account object is re-read from the response.
    const user = await request<Account>('/me/password', {
      method: 'POST',
      body: { currentPassword, newPassword },
      quiet401: true,
    })
    set({ status: 'authenticated', user })
  },

  async updateName(name) {
    // The answer may be the account or nothing at all — both are fine: the
    // name is what we sent, the rest of the account did not move.
    const answer = await request<Account | undefined>('/me', { method: 'PATCH', body: { name } })
    set((state) => ({ user: asAccount(answer) ?? (state.user && { ...state.user, name }) }))
  },

  async checkUsername(candidate, signal) {
    const wire = await request<{ handle: string; status: string; reason: string | null; suggestions: string[] | null }>(
      '/me/username/available',
      { query: { username: candidate }, signal },
    )
    return {
      handle: wire.handle,
      status: wire.status as HandleCheck['status'],
      reason: wire.reason ?? undefined,
      suggestions: wire.suggestions ?? [],
    }
  },

  async updateUsername(username) {
    const answer = await request<Account | undefined>('/me/username', { method: 'POST', body: { username } })
    set((state) => ({ user: asAccount(answer) ?? (state.user && { ...state.user, username }) }))
  },

  async updatePage(patch) {
    const answer = await request<Account | undefined>('/me', { method: 'PATCH', body: patch })
    set((state) => ({ user: asAccount(answer) ?? (state.user && { ...state.user, ...patch }) }))
  },

  syncLocale(lang, chosen) {
    localeQueue = localeQueue
      .then(async () => {
        const { status, user } = get()
        if (status !== 'authenticated' || !user) return
        const patch = localePatch(user, lang, chosen, browserTimeZone())
        if (!patch) return
        const answer = await request<Account | undefined>('/me', { method: 'PATCH', body: patch, quiet401: true })
        set((state) =>
          state.user?.id === user.id ? { user: asAccount(answer) ?? { ...state.user, ...patch } } : state,
        )
      })
      // Nothing to show and nothing to retry: the server keeps what it had,
      // letters go out as they did, and the next sign-in or switch asks again.
      .catch(() => {})
    return localeQueue
  },
}))

// A 401 from any call — not just /me — means the session is gone.
onUnauthorized(() => {
  if (useSession.getState().status !== 'anonymous') {
    useSession.setState({ status: 'anonymous', user: null })
  }
})
